import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AllowedGuard } from 'components/Tvendo/Common/UserAction';
import { ConstantPermissions } from 'contexts/useConstant';
import { makeStyles } from '@material-ui/core/styles';
import { Assignment, Create, FileCopySharp, Visibility } from '@material-ui/icons';
import Button from 'components/CustomButtons/Button.js';
import { Add } from '@material-ui/icons';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardIcon from 'components/Card/CardIcon.js';
import CardHeader from 'components/Card/CardHeader.js';
import ReactTable from 'components/ReactTable/ReactTableV2.js';
import { cardTitle } from 'assets/jss/material-dashboard-pro-react.js';
import { TextField } from '@material-ui/core';
import LoadingSpinner from 'components/Tvendo/Common/LoadingSpinner';
import { wsFindBy, wsUpdate, wsPost } from 'services/WService.js';
import moment from 'moment';
import { Toasty, useToasty } from 'hooks/Toasty';
import ButtonDelete from 'hooks/ButtonDelete';
import FilterCustom from 'components/Tvendo/Common/FilterCustom';
import { WithApp } from 'contexts/appContext';
import { useModal } from 'hooks/useModal';
import Modal from './Modal';

const styles = {
  cardIconTitle: {
    ...cardTitle,
    marginTop: '15px',
    marginBottom: '0px'
  }
};
const useStyles = makeStyles(styles);

const columns = [
  { Header: '#', accessor: 'num', excelEnabled: false, width: 130 },
  { Header: 'Periodo', accessor: 'Periodo', width: 100 },
  { Header: 'Tipo', accessor: 'type', width: 130 },
  { Header: 'Descripción', accessor: 'description' },
  { Header: 'Zona', accessor: 'zone' },
  { Header: 'Grupo', accessor: 'group' },
  { Header: 'Cuota', accessor: 'quota', width: 180, excelEnabled: false },
  { Header: '', accessor: 'actions', excelEnabled: false, width: 0 },
];

export default WithApp(function IndicatorsClorox(props) {
  const classes = useStyles();

  const [indicadores, setIndicadores] = useState([]);
  const [valueSearch, setValueSearch] = useState({});
  const [dataLoading, setDataLoading] = useState(false);
  const [notify, showNotify] = useToasty();
  const [model, setOpen] = useModal();

  //periodo del filtro como número YYYYMM: es lo único que necesita inicializar
  const [currentPeriod, setCurrentPeriod] = useState(null);

  const handleChange = (id, field, value) => {
    const index = indicadores.findIndex((item) => item.id == id);
    if (index >= 0) {
      indicadores[index][field] = value;
      setIndicadores([...indicadores]);
    }
  };

  async function loadIndicadores(filter) {
    setDataLoading(true);
    let response = await wsFindBy('sale/clorox-indicators', filter);
    setDataLoading(false);
    if (response.success) setIndicadores(response.data);
  }

  async function updateIndicador(id, field, value) {
    setDataLoading(true);
    let response = await wsUpdate('sale/clorox-indicators', id, { Periodo: valueSearch.Periodo, [field]: value });
    setDataLoading(false);
    if (response.success) {
      showNotify(response.message);
      loadIndicadores(valueSearch);
    } else {
      showNotify(response.message, 'error');
    }
  }

  //copia los indicadores del periodo anterior al que está seleccionado en el filtro
  async function transferirRegistros(sourcePeriodo, targetPeriodo) {
    setDataLoading(true);
    let response = await wsPost('sale/clorox-indicators/transferir-registros', {
      SourcePeriodo: sourcePeriodo,
      TargetPeriodo: targetPeriodo,
    });
    setDataLoading(false);

    if (response.success) {
      showNotify(response.message);
      loadIndicadores(valueSearch);
    } else {
      showNotify(response.message, 'error');
    }
  }

  //el periodo viaja como texto YYYY-MM; acá se necesita el número YYYYMM
  const handleFilterChange = (value) => {
    if (!value?.Periodo) return setCurrentPeriod(null);
    setCurrentPeriod(parseInt(moment(value.Periodo).format('YYYYMM'), 10));
  };

  //el mes anterior no es restar 1: en enero hay que saltar de año
  const periodoAnterior = (periodo) =>
    parseInt(moment(String(periodo), 'YYYYMM').subtract(1, 'month').format('YYYYMM'), 10);

  return (
    <GridContainer>
      <Toasty action={notify} />
      {model.open ? (
        <Modal
          model={model}
          setOpen={setOpen}
          showNotify={showNotify}
          reload={() => loadIndicadores(valueSearch)}
          valueSearch={valueSearch}
        />
      ) : undefined}
      <GridItem xs={12}>
        <Card>
          <CardHeader icon>
            <AllowedGuard
              permission={ConstantPermissions.SALE_CUSTOMER_PROVIDER_QUOTES_STORE}
              component={
                <CardIcon color="primary">
                  <FileCopySharp style={{ color: 'white' }} />
                </CardIcon>
              }
            >
              <CardIcon
                onClick={() => setOpen({})}
                pointer="true"
                color={props.themeContext.buttonColorSuccess}
              >
                <Add />
              </CardIcon>
            </AllowedGuard>
            <h4 className={classes.cardIconTitle}>Indicadores Clorox{dataLoading && <LoadingSpinner />}</h4>
          </CardHeader>
          <CardBody>
            <FilterCustom
              getFilter={loadIndicadores}
              getValueSearch={(value) => {
                setValueSearch(value);
                handleFilterChange(value);
              }}
              date
              dateType="month"
              labelDate="Periodo"
              dateField="Periodo"
            />
            <AllowedGuard permission={ConstantPermissions.SALE_CUSTOMER_PROVIDER_QUOTES_STORE}>
              <Button
                color="danger"
                disabled={dataLoading || !currentPeriod}
                onClick={() => transferirRegistros(periodoAnterior(currentPeriod), currentPeriod)}
              >
                Inicializar indicadores
              </Button>
            </AllowedGuard>
            <ReactTable
              excelName="indicadores_clorox"
              formatDate="DD-MM-YYYY"
              defaultWidth={1300}
              columns={columns}
              data={indicadores.map((item, i) => ({
                num: (
                  <div className="actions-right">
                    {i + 1}
                    <AllowedGuard permission={ConstantPermissions.SALE_CUSTOMER_PROVIDER_QUOTES_UPDATE}>
                      <Button
                        justIcon
                        round
                        simple
                        color={props.themeContext.buttonColorWarning}
                        onClick={() => setOpen(item)}
                      >
                        <Create />
                      </Button>
                    </AllowedGuard>
                    <AllowedGuard permission={ConstantPermissions.SALE_CUSTOMER_PROVIDER_QUOTES_DELETED}>
                      <ButtonDelete
                        entity="sale/clorox-indicators"
                        id={item.id}
                        reload={() => {
                          setDataLoading(true);
                          loadIndicadores(valueSearch).then(() => setDataLoading(false));
                        }}
                      />
                    </AllowedGuard>
                  </div>
                ),
                Periodo: item.Periodo,
                type: item.type == 'O' ? 'Venta' : item.type == 'C' ? 'Cobertura' : item.type == 'I' ? 'Compra' : item.type == 'U' ? 'Unidades' : '',
                description: item.description,
                zone: item.base_zone_id == null ? 'Toda la empresa' : (item.zone?.description || ''),
                group: item.sale_clorox_group_product_id ? (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span>{item.group_product?.description || '—'}</span>
                  <AllowedGuard
                    permission={ConstantPermissions.SALE_CUSTOMER_PROVIDER_QUOTES_SHOW}
                    component={item.quota}
                  >
                    <Button justIcon round simple color={props.themeContext.buttonColorWarning}>
                      <Link to={`../grupo-productos-clorox/${item.sale_clorox_group_product_id}`}>
                        <Visibility />
                      </Link>
                    </Button>
                  </AllowedGuard>
                  </div>
                ) : '—',
                quota: (
                  <AllowedGuard
                    permission={ConstantPermissions.SALE_CUSTOMER_PROVIDER_QUOTES_UPDATE}
                    component={item.quota}
                  >
                    <TextField
                      fullWidth
                      type="number"
                      name="cuota"
                      margin="normal"
                      value={item.quota || ''}
                      onChange={(e) => handleChange(item.id, 'quota', e.target.value)}
                      onBlur={(e) => updateIndicador(item.id, 'quota', e.target.value)}
                    />
                  </AllowedGuard>
                ),
                actions: '',
              }))}
            />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
});
